import { useMachine } from '@xstate/react';
import { AxiosError } from 'axios';
import { v4 } from 'uuid';
import { assign, createMachine, send } from 'xstate';

import { Address, Suggestion, SuggestionsResponsePayload } from 'api/api';

import { apiInstance } from '~src/global-state';

import { complementAddress } from '~address-form/utils/format';

import { PlaceQueryItem } from '../../types';

interface createMachineProps {
  country: string;
  locale: string;
  checkout_session_id: string;
  inputValue?: string;
}

export const createAutocompleteMachine = ({
  country,
  locale,
  checkout_session_id,
  inputValue = '',
}: createMachineProps) =>
  createMachine(
    {
      predictableActionArguments: true,
      id: 'autocomplete',
      initial: 'blurred',
      context: {
        places: [],
        error: null,
        inputValue: inputValue,
        token: v4(),
        selectedPlaceId: null,
        country: country,
        locale: locale,
        checkout_session_id,
        placeSelected: false,
        provider: undefined,
        containerId: undefined,
      },
      tsTypes: {} as import('./machine.typegen').Typegen0,
      schema: { context: {} as Context, events: {} as Events, services: {} as Services },
      states: {
        focused: {
          initial: 'idle',
          on: {
            BLUR: {
              target: 'blurred',
            },
            KEYSTROKE: [
              {
                target: '.typing',
                cond: 'eventQueryIsEmpty',
                // When user clears the whole input with backspace
                actions: ['resetContext', 'resetContainerId'],
              },
              {
                target: '.debouncing',
                cond: 'typingAfterPlaceSelected',
                internal: false,
                actions: ['resetSearchContext', 'updateInputValue', 'resetContainerId'],
              },
              {
                target: '.debouncing',
                cond: 'eventQueryLongerThan2',
                // External event resets the timer on 'debouncing' on each keystroke
                internal: false,
                actions: ['updateInputValue', 'resetContainerId'],
              },
              {
                target: '.typing',
                cond: 'eventQueryShorterOrEqual2',
                actions: ['updateInputValue', 'resetContainerId'],
              },
            ],
            SELECT_PLACE: {
              actions: ['setFinalInputValue', 'resetPlacesContext', 'setSelectedPlaceId'],
              target: 'requestingPlaceDetails',
            },
            SELECT_PLACES_CONTAINER: {
              actions: ['setSelectedContainerId'],
              target: '.loadingPlaces',
            },
            CLEAR_INPUT: {
              target: '.idle',
              actions: ['resetContext', 'resetContainerId'],
            },
          },
          states: {
            idle: {
              always: [
                {
                  target: 'placeSelected',
                  cond: 'placeIsSelected',
                },
                {
                  target: 'error',
                  cond: 'errorIsNotNil',
                },
                {
                  target: 'empty',
                  cond: 'contextPlacesAreEmptyAndQueryIsNot',
                },
                { target: 'placesLoaded', cond: 'contextPlacesAreNotEmpty' },
              ],
            },
            debouncing: {
              after: {
                200: [
                  {
                    target: 'loadingPlaces',
                  },
                ],
              },
            },
            loadingPlaces: {
              invoke: {
                src: 'getPlacesService',
                onDone: [
                  {
                    target: 'placesLoaded',
                    cond: 'eventPlacesAreNotEmpty',
                    actions: ['updatePlacesContext', 'resetError'],
                  },
                  { target: 'empty', actions: ['updatePlacesContext', 'resetError'] },
                ],
                onError: { target: 'error', actions: 'setError' },
              },
            },
            placesLoaded: {},
            placeSelected: {},
            typing: {},
            empty: {
              on: {
                CLEAR_INPUT: {
                  actions: ['resetContext', 'resetContainerId'],
                },
              },
            },
            error: {},
          },
        },
        blurred: {
          on: {
            FOCUS: 'focused',
            CLEAR_INPUT: {
              actions: ['resetContext', 'resetContainerId'],
            },
          },
        },
        requestingPlaceDetails: {
          invoke: {
            src: 'getPlacesDetailsService',
            onDone: {
              target: 'focused',
              actions: [
                'sendSelectPlaceEvent',
                'resetContext',
                'setInputValue',
                'setPlaceSelectedTrue',
                'resetToken',
              ],
            },
            onError: {
              target: 'focused.error',
              actions: ['resetPlacesContext', 'resetToken'],
            },
          },
        },
      },
      on: {
        CLEAR_EVERYTHING: {
          target: 'blurred',
          actions: ['resetContext', 'resetContainerId'],
        },
      },
    },
    {
      services: {
        getPlacesService: async ctx => {
          const { data } = await apiInstance.v4.addressSuggestionsPost(ctx.checkout_session_id, {
            input: ctx.inputValue,
            country: ctx.country,
            locales: [ctx.locale],
            sessiontoken: ctx.token,
            container_id: ctx.containerId,
          });
          return { suggestions: data.suggestions || [], provider: data.provider };
        },
        getPlacesDetailsService: async ctx => {
          const { data } = await apiInstance.v4.addressDetailsPost(ctx.checkout_session_id, {
            id: ctx.selectedPlaceId as string,
            locales: [ctx.locale],
            sessiontoken: ctx.token,
            provider: ctx.provider,
          });
          return data.address || {};
        },
      },
      actions: {
        updateInputValue: assign({ inputValue: (_, e) => e.query }),
        setFinalInputValue: assign({ inputValue: (_, e) => e.address }),
        setSelectedPlaceId: assign({ selectedPlaceId: (_, e) => e.placeId }),
        setSelectedContainerId: assign({ containerId: (_, e) => e.containerId }),
        updatePlacesContext: assign({
          places: (_, e) => e.data.suggestions,
          provider: (_, e) => e.data.provider,
        }),
        resetPlacesContext: assign({ places: _ => [] }),
        resetContext: assign(ctx => ({
          ...ctx,
          places: [],
          inputValue: '',
          error: null,
          placeSelected: false,
        })),
        resetToken: assign(ctx => ({ ...ctx, token: v4() })),
        resetContainerId: assign({ containerId: undefined }), // used for Loqate's container (address collections)
        resetError: assign({ error: _ => null }),
        setError: assign({
          error: (_, e) => (e.data as AxiosError).message,
        }),
        setPlaceSelectedTrue: assign({ placeSelected: _ => true }),
        resetSearchContext: assign(ctx => ({
          ...ctx,
          places: [],
          error: null,
          placeSelected: false,
        })),
        sendSelectPlaceEvent: send((ctx, { data }) => ({
          ...ctx,
          type: 'GET_PLACE_DETAILS_REQUEST_SUCCESS',
          data,
        })),
        setInputValue: assign({
          inputValue: (ctx, e) => complementAddress(e.data, ctx.country).address_lines?.[0] || '',
        }),
      },
      guards: {
        eventQueryIsEmpty: (_, e) => e.query.length === 0,
        typingAfterPlaceSelected: ctx => ctx.placeSelected && ctx.inputValue.length > 2,
        eventQueryLongerThan2: (_, e) => e.query.length > 2,
        eventQueryShorterOrEqual2: (_, e) => e.query.length <= 2,
        placeIsSelected: ctx => ctx.placeSelected,
        errorIsNotNil: ctx => !!ctx.error,
        contextPlacesAreNotEmpty: ctx => ctx.places?.length > 0,
        contextPlacesAreEmptyAndQueryIsNot: ctx =>
          ctx.places?.length === 0 && ctx.inputValue.length > 0,
        eventPlacesAreNotEmpty: (_, e) => e.data.suggestions.length > 0,
      },
    }
  );

export type Context = {
  places: PlaceQueryItem[];
  inputValue: string;
  token: string;
  error: null | string;
  selectedPlaceId: null | string;
  country: string;
  locale: string;
  checkout_session_id: string;
  placeSelected: boolean;
  provider?: SuggestionsResponsePayload['provider'];
  containerId?: string;
};

export type Events =
  | { type: 'KEYSTROKE'; query: string }
  | { type: 'STOPPED_TYPING' }
  | { type: 'CLEAR_INPUT' }
  | { type: 'SELECT_PLACE'; address: string; placeId: string }
  | { type: 'SELECT_PLACES_CONTAINER'; address: string; containerId: string }
  | {
      type: 'GET_PLACE_DETAILS_REQUEST_SUCCESS';
      data: Address;
    }
  | { type: 'GET_PLACE_DETAILS_REQUEST_ERROR'; err: any }
  | { type: 'FOCUS' }
  | { type: 'BLUR' }
  | { type: 'CLEAR_EVERYTHING' };

export type Services = {
  getPlacesService: {
    data: { provider: SuggestionsResponsePayload['provider']; suggestions: Suggestion[] };
  };
  getPlacesDetailsService: { data: Address };
};

export const useAutocompleteMachine = (props: createMachineProps) =>
  useMachine(createAutocompleteMachine(props));

export type AutocompleteMachine = ReturnType<typeof createAutocompleteMachine>;

export type GetPlaceDetailsRequestSuccess = {
  type: 'GET_PLACE_DETAILS_REQUEST_SUCCESS';
  data: Address;
};
